import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
} from '@mui/material';
import {
  AddShoppingCart as AddShoppingCartIcon,
  Check as CheckIcon,
  ShoppingCart,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { trackAddToCart } from '../../utils/googleTag';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import {
  checkEmployeeFirstPurchaseDiscount,
  applyDiscount
} from '../../utils/discountHelper';

interface SimpleAddToCartButtonProps {
  market: {
    id: string;
    name: string;
  };
  selectedPrice?: {
    priceId: string;
    amount: number;
    interval: 'month' | 'year';
    productId: string;
  };
  onAddToCart?: (item: any) => void | { success: boolean; error?: string };
  variant?: 'text' | 'outlined' | 'contained';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  hidePrice?: boolean;
  disabled?: boolean;
  isInCart?: boolean;
  productName?: string; // Optional custom product name
}

// Helper function to format price
const formatPrice = (price: number, currency: string = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(price / 100); // Convert from cents to dollars
};

export const SimpleAddToCartButton: React.FC<SimpleAddToCartButtonProps> = ({
  market,
  selectedPrice,
  onAddToCart,
  variant = 'contained',
  size = 'medium',
  fullWidth = false,
  hidePrice = false,
  disabled = false,
  isInCart = false,
  productName,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [added, setAdded] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { permissions } = useUserPermissions();
  
  // Apply discount to price if eligible (only for monthly plans)
  const getDiscountedPrice = (amount: number, interval?: 'month' | 'year') => {
    const discountInfo = checkEmployeeFirstPurchaseDiscount(permissions, interval);
    return discountInfo.isEligible 
      ? applyDiscount(amount, discountInfo.discountPercentage)
      : amount;
  };

  const handleAddToCart = async () => {
    // If item is already in cart, navigate to checkout
    if (isInCart) {
      navigate('/checkout');
      return;
    }

    if (!selectedPrice) {
      setErrorMessage('Please select a pricing option');
      setErrorModalOpen(true);
      return;
    }

    setLoading(true);

    try {
      // Show loading state for 1000ms for consistent UX
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Call the parent's add to cart handler if provided
      if (onAddToCart) {
        const interval = selectedPrice.interval === 'month' ? 'monthly' : 'yearly';

        // Apply discount to the price being added to cart (pass interval for monthly check)
        const discountedAmount = getDiscountedPrice(selectedPrice.amount, selectedPrice.interval);

        const result = onAddToCart({
          marketId: market.id,
          marketName: market.name,
          priceId: selectedPrice.priceId,
          price: discountedAmount,  // Use discounted price
          productId: selectedPrice.productId,
          productName: productName || market.name, // Use custom product name or default to market name
          interval: interval,
          productType: 'subscription' // This is a subscription product
        });

        // Check if the result is an object with success property (new API)
        // or undefined (old API for backward compatibility)
        if (result && typeof result === 'object' && 'success' in result) {
          if (!result.success) {
            setErrorMessage(result.error || 'Failed to add item to cart');
            setErrorModalOpen(true);
            setLoading(false);
            return;
          }
        }
      }

      // Track add to cart event for Google Ads (use discounted price)
      const discountedAmount = getDiscountedPrice(selectedPrice.amount, selectedPrice.interval);
      trackAddToCart(
        selectedPrice.priceId,
        market.name,
        discountedAmount,
        'USD'
      );

      setAdded(true);

      // Reset the "added" state after 2 seconds
      setTimeout(() => {
        setAdded(false);
      }, 2000);
    } catch (error) {
      console.error('Error adding to cart:', error);
      setErrorMessage('Failed to add item to cart. Please try again.');
      setErrorModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const getButtonText = () => {
    if (isInCart) {
      return 'In Cart - Go to Checkout';
    }
    if (added) {
      return 'Added to Cart';
    }
    if (hidePrice || !selectedPrice) {
      return 'Add to Cart';
    }
    // Show discounted price on button (pass interval for monthly check)
    const discountedAmount = getDiscountedPrice(selectedPrice.amount, selectedPrice.interval);
    return `Add to Cart - ${formatPrice(discountedAmount)}`;
  };

  const getButtonIcon = () => {
    if (isInCart) {
      return <ShoppingCart />;
    }
    if (added) {
      return <CheckIcon />;
    }
    return <AddShoppingCartIcon />;
  };

  return (
    <>
      <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
        <Button
          variant={variant}
          size={size}
          fullWidth={fullWidth}
          onClick={handleAddToCart}
          disabled={loading || disabled || added}
          startIcon={loading ? <CircularProgress size={20} /> : getButtonIcon()}
          color={added ? 'success' : isInCart ? 'warning' : 'primary'}
          sx={{
            transition: 'all 0.3s ease',
            ...(added && {
              backgroundColor: 'success.light',
              '&:hover': {
                backgroundColor: 'success.light',
              },
            }),
            ...(isInCart && {
              backgroundColor: '#F5A800',
              color: 'white',
              '&:hover': {
                backgroundColor: '#C08500',
              },
            }),
          }}
        >
          {getButtonText()}
        </Button>
      </Box>

      {/* Error Modal */}
      <Dialog
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: 'error.main' }}>
          Cannot Add to Cart
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1, mb: 3 }}>
            {errorMessage}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={() => setErrorModalOpen(false)}
              sx={{ minWidth: 100 }}
            >
              OK
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};